import React, { useState, useEffect } from "react";
import axios from "axios";
import getAPI from "../../service/getAPI";
import { Button } from "react-bootstrap";

import ProductTable from "./ProductTable";
import ProductFormModal from "./ProductFormModal";

function ProductList() {
  const { master } = getAPI();
  
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem("productPageSize")) || 10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // 🟢 NEW: Branch Price Editing State
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [branchPrice, setBranchPrice] = useState("");

  const userType = localStorage.getItem("user_type") || "store";
  const storeId = Number(localStorage.getItem("store_id") || 0);
  
  // 🟢 Tracks if the user has dragged and dropped a row
  const [sequenceModified, setSequenceModified] = useState(false); 
  const [isSavingSequence, setIsSavingSequence] = useState(false);

  const [formData, setFormData] = useState({
    product_id: 0,
    product_name: "",
    product_weight: "",
    product_price: "",
    product_sequence: 1,
    is_active: 1,
    product_image: "",
  });

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (debouncedSearch !== searchInput) {
        setDebouncedSearch(searchInput);
        setPageIndex(1);
      }
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchInput, debouncedSearch]);


  useEffect(() => {
    loadData();
    localStorage.setItem("productPageSize", pageSize);
    setSequenceModified(false); // Reset sequence flag on page load
  }, [pageIndex, pageSize, debouncedSearch]); 

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.post(master.productSelectAll, {
        pageIndex: pageIndex, 
        pageSize: pageSize, 
        searchParam: { global_search: debouncedSearch, is_admin: true },
      });
      if (res.data.flag === 1) {
        setList(res.data.data.result);
        setTotalPages(Math.ceil(res.data.data.count / pageSize));
        setTotalCount(res.data.data.count);
      } else {
        setList([]);
        setTotalPages(0);
        setTotalCount(0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (product) => {
    if (window.confirm(`Are you sure you want to delete ${product.product_name}?`)) {
      try {
        const res = await axios.post(master.productDelete, { product_id: product.product_id });
        if (res.data.flag === 1) loadData();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = new FormData();
      if (isEditing) payload.append("product_id", formData.product_id);
      payload.append("product_name", formData.product_name);
      payload.append("product_weight", formData.product_weight);
      payload.append("product_price", formData.product_price);
      payload.append("product_discount_price", formData.product_discount_price || 0);
      payload.append("product_sequence", formData.product_sequence);
      payload.append("is_active", formData.is_active);
      
      if (selectedFile) {
        payload.append("product_image", selectedFile);
      } else if (isEditing && formData.product_image) {
        payload.append("product_image", formData.product_image);
      }

      const endpoint = isEditing ? master.productUpdate : master.productInsert;
      const res = await axios.post(endpoint, payload, { headers: { "Content-Type": "multipart/form-data" } });

      if (res.data.flag === 1) {
        setShowModal(false);
        loadData(); 
      } else {
        alert(res.data.message || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // 🟢 NEW: Save Sequence Function
  const handleSaveSequence = async () => {
    setIsSavingSequence(true);
    try {
      // Create an array of promises to update the sequence of each affected item
      const updatePromises = list.map((prod, index) => {
        const newSequence = index + 1 + ((pageIndex - 1) * pageSize);
        
        // Only update if the sequence actually changed
        if (prod.product_sequence !== newSequence) {
          const payload = new FormData();
          payload.append("product_id", prod.product_id);
          payload.append("product_name", prod.product_name);
          payload.append("product_weight", prod.product_weight);
          payload.append("product_price", prod.product_price);
          payload.append("product_discount_price", prod.product_discount_price || 0);
          payload.append("product_sequence", newSequence);
          payload.append("is_active", prod.is_active);
          if (prod.product_image) payload.append("product_image", prod.product_image);

          return axios.post(master.productUpdate, payload, { headers: { "Content-Type": "multipart/form-data" } });
        }
        return null;
      }).filter(p => p !== null); // Remove nulls (items that didn't change sequence)

      await Promise.all(updatePromises);
      alert("Sequence updated successfully!");
      setSequenceModified(false);
      loadData();
    } catch (error) {
      console.error("Failed to update sequences:", error);
      alert("Failed to save new sequence.");
    } finally {
      setIsSavingSequence(false);
    }
  };

  const openModal = (product = null) => {
    setIsEditing(!!product);
    setFormData(product ? { ...product } : { product_id: 0, product_name: "", product_weight: "", product_price: "", product_sequence: 1, is_active: 1, product_image: "" });
    setSelectedFile(null);
    setShowModal(true);
  };

  const openPriceModal = (product) => {
    setEditingProduct(product);
    const hasBranchPrice = product.branch_price !== null && product.branch_price !== undefined;
    const branchPrice = hasBranchPrice ? Number(product.branch_price) : Number(product.product_price || 0);
    const discountPrice = Number(product.product_discount_price || 0);
    const effectivePrice = hasBranchPrice ? branchPrice : (discountPrice > 0 ? discountPrice : branchPrice);
    setBranchPrice(effectivePrice);
    setShowPriceModal(true);
  };

  const handleUpdateBranchPrice = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const res = await axios.post(`${master.productSelectAll.replace('retail_apiselectall', 'update-branch-price')}`, {
        store_id: storeId,
        product_id: editingProduct.product_id,
        branch_price: branchPrice
      });
      if (res.data.flag === 1) {
        setShowPriceModal(false);
        loadData();
      } else {
        alert(res.data.message || "Failed to update price.");
      }
    } catch (err) {
      console.error(err);
      alert("Error updating price.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div>
          <h1>Products</h1>
          <nav><ol className="breadcrumb"><li className="breadcrumb-item active">Product Management</li></ol></nav>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        {/* 🟢 ADD BUTTON MOVED TO HEADER */}
        <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          
          <div className="input-group" style={{ maxWidth: "350px" }}>
            <span className="input-group-text bg-light border-end-0"><i className="bi bi-search text-muted"></i></span>
            <input 
              type="text" 
              className="form-control border-start-0 ps-0 bg-light" 
              placeholder="Search products..." 
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button className="btn btn-light text-muted border border-start-0" onClick={() => setSearchInput("")}>
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>

          <div className="d-flex gap-2">
            {/* 🟢 Shows only if they dragged a row */}
            {sequenceModified && (
              <Button variant="warning" className="shadow-sm fw-bold text-dark" onClick={handleSaveSequence} disabled={isSavingSequence}>
                {isSavingSequence ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-list-check me-1"></i>}
                Save Sequence
              </Button>
            )}
            
            {(userType === "admin" || storeId === 1) && (
              <Button variant="success" className="shadow-sm fw-bold" onClick={() => openModal()}>
                <i className="bi bi-plus-circle me-1"></i> Add New Product
              </Button>
            )}
          </div>
        </div>

        <div className="card-body pt-0">
          <ProductTable 
            list={list}
            setList={setList} // Passed down to allow DnD to update local state
            loading={loading} 
            onEdit={openModal} 
            onDelete={handleDelete} 
            onSequenceChanged={setSequenceModified} // Passed down to trigger the "Save" button
            userType={userType}
            storeId={storeId}
            onEditPrice={openPriceModal}
          />
        </div>
        
        {/* Pagination Footer */}
        <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center flex-wrap py-3">
          <div className="text-muted small mb-2 mb-md-0">
            {totalCount === 0 ? <span>No products to display</span> : <span>Showing <strong className="text-dark">{(pageIndex - 1) * pageSize + 1}</strong> to <strong className="text-dark">{Math.min(pageIndex * pageSize, totalCount)}</strong> of <strong className="text-dark">{totalCount}</strong> entries</span>}
          </div>
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center me-4">
              <label htmlFor="pageSize" className="text-muted small me-2 mb-0">Show:</label>
              <select id="pageSize" className="form-select form-select-sm" style={{ width: "75px" }} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPageIndex(1); }}>
                <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
              </select>
            </div>
            {totalPages > 1 && (
              <div className="btn-group shadow-sm">
                <button className="btn btn-sm btn-outline-primary" onClick={() => setPageIndex(pageIndex - 1)} disabled={pageIndex === 1}><i className="bi bi-chevron-left"></i> Prev</button>
                <span className="btn btn-sm btn-primary disabled text-white" style={{ opacity: 1 }}>Page {pageIndex} of {totalPages}</span>
                <button className="btn btn-sm btn-outline-primary" onClick={() => setPageIndex(pageIndex + 1)} disabled={pageIndex === totalPages}>Next <i className="bi bi-chevron-right"></i></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ProductFormModal show={showModal} onHide={() => setShowModal(false)} isEditing={isEditing} formData={formData} setFormData={setFormData} setSelectedFile={setSelectedFile} onSubmit={handleSubmitForm} isSaving={isSaving}/>

      {/* 🟢 NEW: Branch Price Update Modal */}
      {showPriceModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Edit Branch Price</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowPriceModal(false)}></button>
              </div>
              <div className="modal-body">
                <p><strong>Product:</strong> {editingProduct?.product_name}</p>
                <form onSubmit={handleUpdateBranchPrice}>
                  <div className="mb-3">
                    <label className="form-label">Selling Price</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={branchPrice} 
                      onChange={(e) => setBranchPrice(e.target.value)}
                      required 
                      step="0.01"
                    />
                  </div>
                  <div className="text-end">
                    <button type="button" className="btn btn-secondary me-2" onClick={() => setShowPriceModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={isSaving}>
                      {isSaving ? "Saving..." : "Save Price"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

    </main>
  );
}

export default ProductList;